import { getSupabaseClient, isSupabaseConfigured } from '../lib/supabase';
import { paymentSessionService } from './paymentSessionService';
import { currencyService } from './currencyService';

export type ClientStatus = 'pending' | 'active' | 'completed';
export type ClientSource = 'manual' | 'payment';

export interface Client {
  id: string;
  name: string;
  email: string;
  phone?: string;
  company?: string;
  address?: string;
  companyNumber?: string;
  purchaseDate: string;
  serviceId: string;
  serviceName: string;
  status: ClientStatus;
  notes?: string;
  totalAmount: number;
  currencyCode: string;
  source: ClientSource;
  lastContactAt?: string;
}

interface ClientRow {
  client_key: string;
  name: string;
  email: string;
  phone: string | null;
  company: string | null;
  address: string | null;
  company_number: string | null;
  purchase_date: string | null;
  service_key: string | null;
  service_name: string | null;
  status: ClientStatus | null;
  notes: string | null;
  total_amount: number | string | null;
  currency_code: string | null;
  source: ClientSource | null;
  last_contact_at: string | null;
}

let clientsCache: Client[] | null = null;

const generateClientId = () => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }

  return `client_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
};

const normalizeDate = (value?: string | null): string => {
  if (!value) {
    return new Date().toISOString();
  }

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return new Date().toISOString();
  }

  return parsed.toISOString();
};

const sortClients = (clients: Client[]): Client[] =>
  [...clients].sort(
    (left, right) =>
      new Date(right.purchaseDate).getTime() - new Date(left.purchaseDate).getTime()
  );

const mapClientRow = (row: ClientRow): Client => ({
  id: row.client_key,
  name: row.name,
  email: row.email,
  phone: row.phone ?? '',
  company: row.company ?? '',
  address: row.address ?? '',
  companyNumber: row.company_number ?? '',
  purchaseDate: normalizeDate(row.purchase_date),
  serviceId: row.service_key ?? '',
  serviceName: row.service_name ?? '',
  status: row.status ?? 'pending',
  notes: row.notes ?? '',
  totalAmount: Number(row.total_amount ?? 0),
  currencyCode: row.currency_code ?? 'EUR',
  source: row.source ?? 'manual',
  lastContactAt: row.last_contact_at ? normalizeDate(row.last_contact_at) : '',
});

const sanitizeClient = (client: Client): Client => ({
  id: client.id || generateClientId(),
  name: client.name.trim(),
  email: client.email.trim().toLowerCase(),
  phone: client.phone?.trim() || '',
  company: client.company?.trim() || '',
  address: client.address?.trim() || '',
  companyNumber: client.companyNumber?.trim() || '',
  purchaseDate: normalizeDate(client.purchaseDate),
  serviceId: client.serviceId?.trim() || '',
  serviceName: client.serviceName?.trim() || '',
  status: client.status || 'pending',
  notes: client.notes?.trim() || '',
  totalAmount: Number(client.totalAmount || 0),
  currencyCode: client.currencyCode?.trim().toUpperCase() || 'EUR',
  source: client.source || 'manual',
  lastContactAt: client.lastContactAt ? normalizeDate(client.lastContactAt) : '',
});

export const createEmptyClient = (): Client => ({
  id: generateClientId(),
  name: '',
  email: '',
  phone: '',
  company: '',
  address: '',
  companyNumber: '',
  purchaseDate: new Date().toISOString(),
  serviceId: '',
  serviceName: '',
  status: 'pending',
  notes: '',
  totalAmount: 0,
  currencyCode: currencyService.getDisplayCurrencyCode(),
  source: 'manual',
  lastContactAt: '',
});

export async function getAllClients(forceRefresh = false): Promise<Client[]> {
  if (!forceRefresh && clientsCache !== null) {
    return clientsCache;
  }

  if (!isSupabaseConfigured()) {
    throw new Error(
      'Supabase n’est pas configuré pour les clients. Configure VITE_SUPABASE_URL et VITE_SUPABASE_ANON_KEY.'
    );
  }

  const supabase = getSupabaseClient();
  const { data, error } = await supabase
    .from('csd_clients')
    .select(`
      client_key,
      name,
      email,
      phone,
      company,
      address,
      company_number,
      purchase_date,
      service_key,
      service_name,
      status,
      notes,
      total_amount,
      currency_code,
      source,
      last_contact_at
    `)
    .or('is_active.is.null,is_active.eq.true')
    .order('purchase_date', { ascending: false })
    .order('created_at', { ascending: false });

  if (error) {
    throw error;
  }

  clientsCache = sortClients(((data ?? []) as ClientRow[]).map(mapClientRow));
  return clientsCache;
}

export async function saveClient(client: Client): Promise<Client> {
  const sanitized = sanitizeClient(client);

  if (!sanitized.name || !sanitized.email) {
    throw new Error('Le nom et l’email sont obligatoires.');
  }

  if (!isSupabaseConfigured()) {
    throw new Error(
      'Supabase n’est pas configuré pour les clients. Configure VITE_SUPABASE_URL et VITE_SUPABASE_ANON_KEY.'
    );
  }

  const supabase = getSupabaseClient();
  const { error } = await supabase.from('csd_clients').upsert(
    {
      client_key: sanitized.id,
      name: sanitized.name,
      email: sanitized.email,
      phone: sanitized.phone || null,
      company: sanitized.company || null,
      address: sanitized.address || null,
      company_number: sanitized.companyNumber || null,
      purchase_date: sanitized.purchaseDate,
      service_key: sanitized.serviceId || null,
      // service_name and notes are NOT NULL in Supabase.
      service_name: sanitized.serviceName || '',
      status: sanitized.status,
      notes: sanitized.notes || '',
      total_amount: sanitized.totalAmount,
      currency_code: sanitized.currencyCode,
      source: sanitized.source,
      last_contact_at: sanitized.lastContactAt || null,
      is_active: true,
    },
    { onConflict: 'client_key' }
  );

  if (error) {
    throw error;
  }

  clientsCache = null;
  return sanitized;
}

export async function removeClient(clientId: string): Promise<void> {
  if (!isSupabaseConfigured()) {
    throw new Error(
      'Supabase n’est pas configuré pour les clients. Configure VITE_SUPABASE_URL et VITE_SUPABASE_ANON_KEY.'
    );
  }

  const supabase = getSupabaseClient();
  const { error } = await supabase
    .from('csd_clients')
    .update({ is_active: false })
    .eq('client_key', clientId);

  if (error) {
    throw error;
  }

  clientsCache = null;
}

export async function captureClientFromPayment(token: string): Promise<Client | null> {
  const session = paymentSessionService.getSession(token);

  if (!session?.customer || !session.customer.email) {
    return null;
  }

  const name = [session.customer.firstName, session.customer.lastName]
    .filter(Boolean)
    .join(' ')
    .trim();

  const client = sanitizeClient({
    id: token,
    name: name || session.customer.email,
    email: session.customer.email,
    phone: session.customer.phone || '',
    company: session.company || '',
    address: '',
    companyNumber: '',
    purchaseDate: session.createdAt || new Date().toISOString(),
    serviceId: session.serviceId || '',
    serviceName: session.serviceName || '',
    status: 'active',
    notes: `Paiement confirme via Mollie (${session.paymentType === 'klarna' ? 'Klarna' : 'Carte bancaire'}).`,
    totalAmount: session.amount || 0,
    currencyCode: session.currencyCode || 'EUR',
    source: 'payment',
    lastContactAt: new Date().toISOString(),
  });

  await saveClient(client);
  return client;
}

export function clearClientCache(): void {
  clientsCache = null;
}

export const clientService = {
  getClients: getAllClients,
  saveClient,
  removeClient,
  createEmptyClient,
  captureClientFromPayment,
  clearCache: clearClientCache,
};
